import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AnimatePresence, motion } from "framer-motion";
import { Icon } from "@iconify/react/dist/iconify.js";
import { toast } from "react-toastify";
import useSound from "use-sound";

import QuizLayout from "./QuizLayout";
import QuestionCard from "./QuestionCard";
import QuizContinueCard from "./QuizContinueCard";
import ConfirmacionSummary from "./ConfirmacionSummary";
import Loader from "../../UI/Loader";
import NoInternetScreen from "../../UI/NoInternetScreen";
import { getFortalezaMeta } from "../../../utils/fortalezaMeta";

import {
  getCurrentConfirmacionThunk,
  sendConfirmacionAnswerThunk,
  getMisTalentosThunk,
  getConfirmacionesCompletadasThunk,
  getConfirmacionSummaryThunk,
} from "../../../store/confirmacion/confirmacion.thunks";
import {
  toggleFrase,
} from "../../../store/confirmacion/confirmacion.slice";

import clickSfx from "./click.mp3";
import "./css/confirmacionQuiz.css";

const ConfirmacionQuiz = () => {
  const dispatch = useDispatch();

  const {
    currentQuestion,
    selectedFrases,
    loading,
    summary,
    summaryLoading,
  } = useSelector((state) => state.confirmacion);

  const [playClick] = useSound(clickSfx, { volume: 0.25 });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasError, setHasError] = useState(false);
  // alreadyCompleted: al cargar la página el quiz ya estaba terminado (regreso posterior) → mostrar summary.
  // justFinished: el usuario acaba de terminar en esta sesión de pantalla → flujo rápido con Continuar.
  const [alreadyCompleted, setAlreadyCompleted] = useState(false);
  const [justFinished, setJustFinished] = useState(false);
  const [isInitialLoading, setIsInitialLoading] = useState(true);

  // Cargar datos iniciales
  useEffect(() => {
    const loadData = async () => {
      try {
        await dispatch(getMisTalentosThunk()).unwrap();
        await dispatch(getConfirmacionesCompletadasThunk()).unwrap();
        const pregunta = await dispatch(getCurrentConfirmacionThunk()).unwrap();

        if (pregunta.todas_completadas) {
          // Regreso posterior a un quiz ya completado — cargar summary.
          setAlreadyCompleted(true);
          await dispatch(getConfirmacionSummaryThunk()).unwrap();
        }
      } catch (error) {
        setHasError(true);
        toast.error("Las confirmaciones de talentos aún no están disponibles", {
          position: "top-center",
          autoClose: 5000,
        });
      } finally {
        setIsInitialLoading(false);
      }
    };

    loadData();
  }, [dispatch]);

  // Fetch de respaldo del summary si aún no está cargado cuando entramos en regreso posterior.
  useEffect(() => {
    if (alreadyCompleted && !summary && !summaryLoading) {
      dispatch(getConfirmacionSummaryThunk());
    }
  }, [alreadyCompleted, summary, summaryLoading, dispatch]);

  // Detectar conexión a internet
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);

    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);

    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  const handleToggleFrase = useCallback((fraseId) => {
    playClick();
    dispatch(toggleFrase(fraseId));
  }, [dispatch, playClick]);

  const finalizarSesion = useCallback(() => {
    toast.success("¡Has completado todas las confirmaciones!", {
      position: "top-center",
      autoClose: 3000,
    });
    setJustFinished(true);
  }, []);

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);

    try {
      const result = await dispatch(
        sendConfirmacionAnswerThunk({ frase_ids: selectedFrases })
      );

      if (result.type.endsWith("/rejected")) {
        toast.error(result.payload || "Error al guardar la respuesta", {
          position: "top-center",
          autoClose: 5000,
        });
        return;
      }

      // Fire-and-forget — no bloquear el spinner esperando la lista de completadas
      dispatch(getConfirmacionesCompletadasThunk());

      const payload = result.payload || {};
      const seCompleto =
        payload.completado === true ||
        payload.todas_completadas === true ||
        payload.pregunta === null;

      if (seCompleto) {
        finalizarSesion();
        return;
      }

      // Fallback: si la respuesta no trajo señal de fin, el próximo current lo confirma.
      const next = await dispatch(getCurrentConfirmacionThunk()).unwrap();
      if (next?.todas_completadas === true || !next?.pregunta) {
        finalizarSesion();
      }
    } catch (error) {
      toast.error("Error al procesar la respuesta", {
        position: "top-center",
        autoClose: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [selectedFrases, dispatch, finalizarSesion]);

  if (!isOnline) {
    return <NoInternetScreen />;
  }

  if (hasError) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <div className="confirmacion-error">
              <Icon icon="mdi:alert-circle" />
              <h3>Funcionalidad en desarrollo</h3>
              <p>Las confirmaciones de talentos estarán disponibles próximamente.</p>
            </div>
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  // Recién completado en esta sesión → celebración + Continuar (flujo rápido).
  // Incluye el caso donde handleSubmit no detectó la señal pero el re-fetch de
  // current sí trajo todas_completadas=true (red de seguridad).
  const terminadoEnSesion =
    justFinished ||
    (!alreadyCompleted &&
      !isInitialLoading &&
      currentQuestion?.todas_completadas === true);

  if (terminadoEnSesion) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <QuizContinueCard
              titulo="¡Confirmación completada!"
              subtitulo="Sigamos con el módulo de fortalezas."
              rutaSiguiente="/fortalezas-quiz"
              botonTexto="Sigamos construyendo tus 7 fortalezas únicas"
            />
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  // Regreso posterior a un quiz ya completado → summary con sus respuestas.
  if (alreadyCompleted) {
    return (
      <div className="dashboard-main-body">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-24">
          <h6 className="fw-semibold mb-0">Mi confirmación de talentos</h6>
          <ul className="d-flex align-items-center gap-2">
            <li className="fw-medium">
              <a
                href="/#/mis-talentos"
                className="d-flex align-items-center gap-1 hover-text-primary"
              >
                <Icon icon="mdi:arrow-left" className="text-lg" />
                Mis Talentos
              </a>
            </li>
          </ul>
        </div>

        {isInitialLoading || loading || summaryLoading ? (
          <div className="card radius-12">
            <div className="card-body p-24">
              <Loader
                center
                size="lg"
                type="dots"
                delay={150}
                text="Cargando resumen..."
              />
            </div>
          </div>
        ) : (
          <ConfirmacionSummary summary={summary} />
        )}
      </div>
    );
  }

  // Loading para quiz activo (carga inicial o transición entre preguntas)
  if ((loading || isInitialLoading || isSubmitting) && !currentQuestion) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <Loader
              overlay
              size="lg"
              type="dots"
              delay={150}
              text="Cargando confirmación..."
            />
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  if (!currentQuestion || !currentQuestion.pregunta) {
    return (
      <QuizLayout>
        <div className="quiz-scope">
          <QuestionCard>
            <Loader
              overlay
              size="lg"
              type="dots"
              delay={150}
              text="Cargando confirmación..."
            />
          </QuestionCard>
        </div>
      </QuizLayout>
    );
  }

  const { pregunta, pregunta_index, total_preguntas, talento_index, total_talentos, frases } = currentQuestion;

  return (
    <QuizLayout>
      <div className="quiz-scope">
        <QuestionCard>
          {(loading || isSubmitting) && (
            <Loader
              overlay
              size="lg"
              type="dots"
              delay={150}
              text={isSubmitting ? "Guardando respuesta..." : "Cargando..."}
            />
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={`${pregunta_index}-${talento_index}`}
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.5, ease: "easeInOut" }}
              style={{ overflow: "hidden" }}
            >
              {/* Header con progreso */}
              <div className="confirmacion-header">
                <div className="confirmacion-progress">
                  <Icon icon="mdi:progress-check" />
                  <span className="confirmacion-progress-text">
                    Pregunta {pregunta_index} / {total_preguntas} · Talento {talento_index} / {total_talentos}
                  </span>
                </div>
              </div>

              {/* Título de fortaleza (según tipo_fortaleza del backend) */}
              {(() => {
                const meta = getFortalezaMeta(pregunta.tipo_fortaleza);
                if (!meta) return null;
                return (
                  <div className="fortaleza-titulo-wrap">
                    <h4 className="fortaleza-titulo">
                      Fortaleza {pregunta.tipo_fortaleza} · {meta.titulo}
                    </h4>
                  </div>
                );
              })()}

              {/* Pregunta */}
              <h3 className="confirmacion-question">{pregunta.pregunta}</h3>

              {/* Frases seleccionables */}
              <div className="confirmacion-frases">
                {frases.map((frase) => {
                  const isSelected = selectedFrases.includes(frase.id);
                  return (
                    <motion.div
                      key={frase.id}
                      className={`confirmacion-frase ${isSelected ? "selected" : ""}`}
                      onClick={() => handleToggleFrase(frase.id)}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <div className="confirmacion-frase-checkbox">
                        {isSelected ? (
                          <Icon icon="mdi:checkbox-marked" />
                        ) : (
                          <Icon icon="mdi:checkbox-blank-outline" />
                        )}
                      </div>
                      <p className="confirmacion-frase-text">{frase.frase}</p>
                    </motion.div>
                  );
                })}
              </div>

              {/* Botón de guardar */}
              <button
                className="confirmacion-submit-btn"
                onClick={handleSubmit}
                disabled={isSubmitting || loading}
              >
                <Icon icon="mdi:content-save" />
                <span>Continuar</span>
              </button>
            </motion.div>
          </AnimatePresence>
        </QuestionCard>
      </div>
    </QuizLayout>
  );
};

export default ConfirmacionQuiz;
